import React, { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import RestHttpClient from '../../../../../common/RestHttpClient';
import {
  ApiException,
  ConditionAdjustmentDecision,
  DealCalculationOverview,
  ISignFinancingCalculationDocumentsResponse,
  MessageSeverity,
} from '../../../../../generated/ApiClient';
import { TakeoverFinalAction, TakeoverStatus } from './TakeoverProgressPanel';
import { TakeoverAction } from './resolveTakeoverAction';

interface ServerMessages {
  global?: {
    severity: MessageSeverity;
    message: string;
    translate: boolean;
    arguments?: string[];
  }[];
}

export const getServerErrorMessage = (messages?: ServerMessages) => {
  const serverError = messages?.global?.find((m) => m.severity === MessageSeverity.Error);
  if (!serverError) return undefined;
  return serverError.translate
    ? i18next.t(`errors.${serverError.message}`, serverError.arguments)
    : serverError.message;
};

export const isForbidden = (err: unknown) =>
  !!err && ApiException.isApiException(err) && err.status === 403;

const readBlobText = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });

const getServerErrorMessageFromBlob = async (blob?: Blob) => {
  if (!blob) return undefined;
  try {
    const envelope = JSON.parse(await readBlobText(blob));
    return getServerErrorMessage(envelope?.messages);
  } catch {
    return undefined;
  }
};

interface UseTakeoverFlowOptions {
  dealId: number;
  refetch: () => void | Promise<void>;
}

export interface UseTakeoverFlowResult {
  status: TakeoverStatus | null;
  inFlightId: number | undefined;
  // The action comes from the cell's fresh render data; re-deriving it here closes over stale rows.
  trigger: (
    dco: DealCalculationOverview,
    action: TakeoverAction,
    finalAction?: TakeoverFinalAction
  ) => (e?: React.MouseEvent) => Promise<void>;
  closeStatus: () => void;
  signDocuments: ISignFinancingCalculationDocumentsResponse[];
  isSignDocumentsOpen: boolean;
  setSignDocumentsOpen: Dispatch<SetStateAction<boolean>>;
}

export function useTakeoverFlow({
  dealId,
  refetch,
}: UseTakeoverFlowOptions): UseTakeoverFlowResult {
  const { t } = useTranslation();
  const [status, setStatus] = useState<TakeoverStatus | null>(null);
  const [inFlightId, setInFlightId] = useState<number | undefined>(undefined);
  const [signDocuments, setSignDocuments] = useState<ISignFinancingCalculationDocumentsResponse[]>(
    []
  );
  const [isSignDocumentsOpen, setSignDocumentsOpen] = useState(false);

  const failDocumentStep = (error: string) =>
    setStatus((s) => (s ? { ...s, document: 'error', error } : s));

  const failureMessage = (err: unknown, fallback: string): string =>
    isForbidden(err) ? t('errors.calculation.takeover.forbidden') : fallback;

  const printDocument = async (calcId: number, conditionsAdjusted: boolean) => {
    const printFailedKey = conditionsAdjusted
      ? 'errors.calculation.takeover.print.failed'
      : 'errors.calculation.takeover.print.failedWithoutAdjustment';
    try {
      const printResult = await RestHttpClient.printTakeoverConditionDocuments(dealId, calcId);
      // A refused print returns HTTP 200 with a JSON error envelope (backend returns Ok() instead of a file).
      // Only download an actual non-empty PDF, otherwise surface a print failure.
      const blob = printResult.data;
      const isPdf =
        !!blob && blob.size > 0 && !blob.type.toLowerCase().includes('application/json');
      if (!isPdf) {
        const serverError = await getServerErrorMessageFromBlob(blob);
        failDocumentStep(serverError ?? t(printFailedKey));
        return;
      }
      const url = window.URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = url;
        a.download = printResult.fileName ?? 'Uebernahmebestaetigung.pdf';
        a.click();
      } finally {
        window.URL.revokeObjectURL(url);
      }
      setStatus((s) => (s ? { ...s, document: 'done' } : s));
    } catch (err) {
      failDocumentStep(failureMessage(err, t(printFailedKey)));
    }
  };

  const signDocument = async (calcId: number) => {
    try {
      const signResult = await RestHttpClient.signTakeoverConditionDocuments(dealId, calcId);
      const documents = signResult.data ?? [];
      if (documents.length === 0) {
        const serverError = getServerErrorMessage(signResult.messages);
        failDocumentStep(serverError ?? t('errors.calculation.takeover.sign.failed'));
        return;
      }
      setStatus((s) => (s ? { ...s, document: 'done' } : s));
      setSignDocuments(documents);
      setSignDocumentsOpen(true);
    } catch (err) {
      failDocumentStep(failureMessage(err, t('errors.calculation.takeover.sign.failed')));
    }
  };

  const trigger =
    (
      dco: DealCalculationOverview,
      action: TakeoverAction,
      finalAction: TakeoverFinalAction = 'print'
    ) =>
    async (e?: React.MouseEvent) => {
      e?.stopPropagation();

      const startsTakeover = action.mode === 'takeover' || action.mode === 'fleet';
      if (!startsTakeover) return;

      const calcId = dco.fundingProductId;
      setInFlightId(calcId);
      setStatus({ finalAction, decredo: 'running', document: 'pending' });

      try {
        const res = await RestHttpClient.triggerTakeoverConditionAdjustment(dealId, calcId);
        if (res.data.timedOut) {
          setStatus((s) => (s ? { ...s, decredo: 'timeout' } : s));
          return;
        }
        if (res.data.decision !== ConditionAdjustmentDecision.OK) {
          const serverError = getServerErrorMessage(res.messages);
          const decredoMessage =
            res.data.decision === ConditionAdjustmentDecision.NOK ? res.data.reason : undefined;
          setStatus((s) =>
            s
              ? {
                  ...s,
                  decredo: 'error',
                  error:
                    decredoMessage ?? serverError ?? t('errors.calculation.takeover.decredoError'),
                }
              : s
          );
          return;
        }
        const conditionsAdjusted = !res.data.recalculationSkipped;
        setStatus((s) => (s ? { ...s, decredo: 'done', document: 'running' } : s));

        if (finalAction === 'sign') {
          await signDocument(calcId);
        } else {
          await printDocument(calcId, conditionsAdjusted);
        }
      } catch (err) {
        // Without this a thrown trigger call would leave a step running and the dialog could never be closed.
        const error = failureMessage(err, t('errors.calculation.takeover.failed'));
        setStatus((s) =>
          s
            ? {
                ...s,
                decredo: s.decredo === 'running' ? 'error' : s.decredo,
                document: s.document === 'running' ? 'error' : s.document,
                error,
              }
            : s
        );
      } finally {
        try {
          await refetch();
        } catch {
          // A failed reload must not turn a finished takeover into an error.
        }
        setInFlightId(undefined);
      }
    };

  return {
    status,
    inFlightId,
    trigger,
    closeStatus: () => {
      setStatus(null);
      setSignDocuments([]);
      setSignDocumentsOpen(false);
    },
    signDocuments,
    isSignDocumentsOpen,
    setSignDocumentsOpen,
  };
}
